import React, { useCallback } from 'react';
import { Row, Collapse, Col } from 'reactstrap';
import BadgeComponent from './RenderBadge';
import NotificationDetails from './NotificationDetails';
import { NotificationActiveTab, NotificationUserStatusConstant } from 'shared/constants';
import { dateTimeFormat, truncateString } from 'GenericForms/Helper';
import { HandleNotificationUserState } from './Notification';
import { queryClient } from 'react-query/queryClient';
import { queryKeyes } from 'shared/queryKeys';

interface NotificationItem {
    id: string;
    notification_id: string;
    title: string;
    message: string;
    category: string;
    priority: string;
    category_name: string;
    category_color_light: string;
    priority_name: string;
    priority_color_light: string;
    date: string;
    vessel_name: string;
    current_user_state_name: string;
    [key: string]: any;
}

interface NotificationTableBodyProps {
    handleNotificationClick: (notification: NotificationItem | null) => void;
    selectedNotification: NotificationItem | null;
    handleBackClick: () => void;
    notificationUserStatusObject: any[];
    notificationUser: any[];
    notificationStateObject: any[];
    activeTab: string;
    count: number;
    setCount: (count: number) => void;
    refetch: () => Promise<any>;
    page: any
}

const NotificationTableBody: React.FC<NotificationTableBodyProps> = ({
    handleNotificationClick,
    selectedNotification,
    handleBackClick,
    notificationUserStatusObject,
    notificationUser,
    activeTab,
    notificationStateObject,
    count,
    setCount,
    refetch,
    page,
}) => {
    const noToast = true;
    const isUnreadNotification = useCallback(
        (notificationBody: NotificationItem): boolean => {
            return (
                notificationBody?.current_user_state_name === 'new' &&
                activeTab === NotificationActiveTab.INBOX
            );
        },
        [activeTab]
    );

    const getRowClasses = useCallback(
        (notificationBody: NotificationItem): string => {
            const baseClasses = 'notification-row';
            return isUnreadNotification(notificationBody)
                ? `${baseClasses} unread-notification`
                : `${baseClasses} read-notification`;
        },
        [isUnreadNotification]
    );

    const handleNotificationClickEnhanced = useCallback(
        async (notificationBody: NotificationItem) => {
            if (selectedNotification?.notification_id === notificationBody.notification_id) {
                handleNotificationClick(null);
                return;
            }
            try {
                await handleNotificationClick(notificationBody);
                if (isUnreadNotification(notificationBody)) {
                    const response = await HandleNotificationUserState(
                        notificationBody,
                        NotificationUserStatusConstant.READ,
                        notificationUserStatusObject,
                        notificationUser,
                        count,
                        setCount,
                        noToast,
                    );
                    if (response?.status === 200) {
                        await Promise.all([
                            queryClient.invalidateQueries(queryKeyes.notification.NotificationInbox.key),
                            queryClient.invalidateQueries(queryKeyes.notification.NotificationSent.key),
                            queryClient.invalidateQueries(queryKeyes.notification.NotificationDraft.key),
                            queryClient.invalidateQueries(queryKeyes.notification.NotificationArchive.key),
                            queryClient.invalidateQueries(queryKeyes.notification.NotificationForLoggedInUser.key),
                        ]);
                        setCount(count + 1);
                        await refetch();
                    }
                }
            } catch (error) {
                console.error('Error handling notification click:', error);
            }
        },
        [
            selectedNotification,
            handleNotificationClick,
            isUnreadNotification,
            notificationUserStatusObject,
            notificationUser,
            count,
            setCount,
            refetch,
            noToast
        ]
    );

    if (!page?.length) {
        return (
            <tbody>
                <tr>
                    <td colSpan={6} className="text-center p-4 text-muted">
                        <div className="d-flex flex-column align-items-center">
                            <i className="fas fa-bell-slash fa-2x mb-2 text-muted"></i>
                            <span>No notifications found</span>
                        </div>
                    </td>
                </tr>
            </tbody>
        );
    }
    return (
        <tbody>
            {page.map((notificationBody: NotificationItem, index: number) => (
                <React.Fragment key={notificationBody.id}>
                    <tr
                        onClick={() => handleNotificationClickEnhanced(notificationBody)}
                        className={getRowClasses(notificationBody)}
                        role="button"
                        tabIndex={0}
                        aria-label={`Notification: ${notificationBody.title}`}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleNotificationClickEnhanced(notificationBody);
                            }
                        }}
                    >
                        <td className="p-2 text-center align-middle">
                            <div className="d-flex align-items-center justify-content-center">
                                {index + 1}
                            </div>
                        </td>
                        <td className="p-2 text-center align-middle">
                            <BadgeComponent
                                text={notificationBody.category_name}
                                badgeColors={notificationBody.category_color_light}
                            />
                        </td>
                        <td className="p-2 text-center align-middle">
                            <BadgeComponent
                                text={notificationBody.priority_name}
                                badgeColors={notificationBody.priority_color_light}
                            />
                        </td>
                        <td className="p-2 align-middle text-center">
                            <span
                                className={isUnreadNotification(notificationBody) ? 'font-weight-bold' : ''}
                                title={new Date(notificationBody.date).toLocaleString()}
                            >
                                {dateTimeFormat(notificationBody.date, false, false, true)}
                            </span>
                        </td>
                        <td className="p-2 align-middle text-center">
                            <span
                                className={isUnreadNotification(notificationBody) ? 'font-weight-bold' : ''}
                                title={notificationBody.vessel_name}
                            >
                                {notificationBody.vessel_name}
                            </span>
                        </td>
                        <td className="p-2 align-middle">
                            <div className="d-flex align-items-center justify-content-between">
                                <span
                                    className={isUnreadNotification(notificationBody) ? 'font-weight-bold' : ''}
                                    title={notificationBody.title}
                                >
                                    {truncateString(notificationBody.title, 40)}
                                </span>
                                <div className="d-flex align-items-center">
                                    {selectedNotification?.notification_id === notificationBody.notification_id && (
                                        <i className="fas fa-chevron-down ml-2 text-primary small-icon" />
                                    )}
                                </div>
                            </div>
                        </td>
                    </tr>
                    {selectedNotification?.notification_id === notificationBody.notification_id && (
                        <tr>
                            <td colSpan={6} className="p-0">
                                <Collapse isOpen={!!selectedNotification} className="notification-details-container">
                                    <Row>
                                        <Col xs="12">
                                            <NotificationDetails
                                                notification={selectedNotification}
                                                handleBackClick={handleBackClick}
                                                notificationUser={notificationUser}
                                                notificationStateObject={notificationStateObject}
                                                notificationUserStatusObject={notificationUserStatusObject}
                                                selectedNotification={selectedNotification}
                                                activeTab={activeTab as string}
                                                count={count}
                                                setCount={setCount}
                                                refetch={refetch}
                                            />
                                        </Col>
                                    </Row>
                                </Collapse>
                            </td>
                        </tr>
                    )}
                </React.Fragment>
            ))}
        </tbody>
    );
};

export default React.memo(NotificationTableBody);
